import { __awaiter, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { styled } from 'styled-components';
import { Box } from "../../box/src";
import { Button } from "../../button/src";
import { CropAndZoom } from "../../crop-and-zoom/src";
import { Modal } from "../../dialog/src";
import { FilePreview } from "../../file-preview/src";
import { Drop } from './components/Drop';
import { UploadContainer } from './components/UploadContainer';
const DEFAULT_ACCEPTED_FILES = ['image/gif', 'image/png', 'image/jpeg'];
export const SingleImageUpload = ({ acceptedFiles = DEFAULT_ACCEPTED_FILES, ariaLabel, canRemoveUpload = true, cropAndZoomEnabled, cropAndZoomProps, dataTestId, emptyStateIconSize = '48px', height = '176px', iconName = 'image', imagePickers, initialPreview = null, isLoading, label, name, onChange, removable = true, skipPreviewGeneration, width = '176px', }) => {
    var _a, _b;
    const inputFileRef = useRef(null);
    const _c = cropAndZoomProps || {}, { modalTitle } = _c, cropAndZoomPropsRest = __rest(_c, ["modalTitle"]);
    const hasInitialPreviewImage = Boolean(initialPreview);
    const [previewImage, setPreviewImage] = useState(initialPreview);
    const [isDirty, setIsDirty] = useState(false);
    const [originalImage, setOriginalImage] = useState(null);
    const [isImagePickersModalOpen, setIsImagePickersModalOpen] = useState(false);
    const [isCropAndZoomModalOpen, setIsCropAndZoomModalOpen] = useState(false);
    const handleResetPreviewImage = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (previewImage) {
            URL.revokeObjectURL(previewImage);
        }
        const newPreviewUrl = hasInitialPreviewImage
            ? removable
                ? null
                : initialPreview
            : null;
        const result = yield (onChange === null || onChange === void 0 ? void 0 : onChange(null, newPreviewUrl));
        if (result === false) {
            return;
        }
        if (typeof result === 'string') {
            setPreviewImage(result);
        }
        else {
            setPreviewImage(newPreviewUrl);
        }
        if (inputFileRef.current) {
            inputFileRef.current.value = '';
        }
        setOriginalImage(null);
        setIsDirty(false);
    }), [
        hasInitialPreviewImage,
        initialPreview,
        onChange,
        previewImage,
        removable,
    ]);
    const readFile = useCallback((image) => __awaiter(void 0, void 0, void 0, function* () {
        setIsImagePickersModalOpen(false);
        let newPreviewUrl;
        let file;
        if (typeof image === 'string') {
            const blob = yield fetch(image).then((r) => r.blob());
            const imageName = image.split('/').pop() || 'image';
            newPreviewUrl = image;
            file = new File([blob], imageName, { type: blob.type });
        }
        else {
            const readerLoadPromise = new Promise((resolve) => {
                if (skipPreviewGeneration) {
                    resolve('');
                    return;
                }
                const reader = new FileReader();
                reader.onload = (fileReadEvent) => __awaiter(void 0, void 0, void 0, function* () {
                    var _a;
                    const dataUrlPreview = (_a = fileReadEvent === null || fileReadEvent === void 0 ? void 0 : fileReadEvent.target) === null || _a === void 0 ? void 0 : _a.result;
                    resolve(dataUrlPreview);
                });
                reader.readAsDataURL(image);
            });
            newPreviewUrl = yield readerLoadPromise;
            file = image;
        }
        const result = yield (onChange === null || onChange === void 0 ? void 0 : onChange(file, newPreviewUrl));
        if (result === false) {
            handleResetPreviewImage();
            return;
        }
        if (typeof result === 'string') {
            setPreviewImage(result);
        }
        else {
            setPreviewImage(newPreviewUrl);
        }
        setIsDirty(true);
    }), [handleResetPreviewImage, onChange, skipPreviewGeneration]);
    const newFileReceived = useCallback((file) => {
        if (file instanceof File && !acceptedFiles.includes(file.type)) {
            return;
        }
        if (cropAndZoomEnabled) {
            setOriginalImage(file);
            setIsCropAndZoomModalOpen(true);
        }
        else {
            readFile(file);
        }
    }, [acceptedFiles, cropAndZoomEnabled, readFile]);
    const handleChangeInputFile = (event) => {
        const { files: filesToRead } = event.currentTarget;
        const file = filesToRead === null || filesToRead === void 0 ? void 0 : filesToRead.item(0);
        if (!file) {
            return;
        }
        newFileReceived(file);
    };
    const handleDrop = ({ files }) => {
        const [file] = files;
        newFileReceived(file);
    };
    const handleCropAndZoomSave = useCallback(({ modifiedImage }) => {
        setIsCropAndZoomModalOpen(false);
        setOriginalImage(null);
        readFile(modifiedImage);
    }, [readFile]);
    const handleCropAndZoomCancel = useCallback(() => {
        setIsCropAndZoomModalOpen(false);
    }, []);
    const clickInputFile = () => {
        if (inputFileRef.current) {
            inputFileRef.current.value = '';
            inputFileRef.current.click();
        }
    };
    const handleEdit = () => {
        if (imagePickers) {
            setIsImagePickersModalOpen(true);
            return;
        }
        clickInputFile();
    };
    useEffect(() => {
        setPreviewImage(initialPreview);
    }, [initialPreview, removable]);
    const canResetPreviewImage = canRemoveUpload && (isDirty || (hasInitialPreviewImage && removable));
    const handleImagePickerModalDismiss = useCallback(() => {
        setIsImagePickersModalOpen(false);
    }, []);
    const pickerComponents = useMemo(() => imagePickers === null || imagePickers === void 0 ? void 0 : imagePickers.map((imagePicker, index) => {
        var _a;
        if (imagePicker === 'file') {
            return (_jsx(Button, { onClick: clickInputFile, children: ((_a = window === null || window === void 0 ? void 0 : window.__) === null || _a === void 0 ? void 0 : _a.call(window, 'Choose an image')) || 'Choose an image' }, "file-picker"));
        }
        const ImagePickerComponent = imagePicker;
        return _jsx(ImagePickerComponent, { onPick: newFileReceived }, index);
    }), [imagePickers, newFileReceived]);
    return (_jsxs(Box, { height: height, width: width, children: [_jsx(DndProvider, { backend: HTML5Backend, children: _jsx(Drop, { height: height, onDrop: handleDrop, width: width, children: ({ isOver }) => (_jsxs(Box, { "data-testid": dataTestId, flexDirection: "column", height: height, position: "relative", width: width, children: [previewImage ? (_jsx(FilePreview, { height: height, image: previewImage, isLoading: isLoading, onClick: handleEdit, onRemove: canResetPreviewImage ? handleResetPreviewImage : undefined, width: width })) : (_jsx(UploadContainer, { ariaLabel: ariaLabel, handleEdit: handleEdit, iconName: iconName, iconSize: emptyStateIconSize, isLoading: isLoading, isOver: isOver, label: label })), _jsx(InputFileHidden, { accept: acceptedFiles.join(','), "aria-hidden": true, "data-testid": "single-image-upload-input-file", hidden: true, name: name, onChange: handleChangeInputFile, ref: inputFileRef, tabIndex: -1, type: "file" })] })) }) }), cropAndZoomEnabled && originalImage && isCropAndZoomModalOpen ? (_jsx(Modal, { dataTestId: "crop-and-zoom-modal", header: modalTitle || ((_a = window === null || window === void 0 ? void 0 : window.__) === null || _a === void 0 ? void 0 : _a.call(window, 'Crop and zoom')) || 'Crop and zoom', isOpen: true, onDismiss: handleCropAndZoomCancel, width: "90vw", children: _jsx(Box, { height: "75vh", children: _jsx(CropAndZoom, Object.assign({ image: originalImage, onCancel: handleCropAndZoomCancel, onSave: handleCropAndZoomSave }, cropAndZoomPropsRest)) }) })) : null, pickerComponents && isImagePickersModalOpen ? (_jsx(Modal, { "data-testid": "image-pickers-modal", header: ((_b = window === null || window === void 0 ? void 0 : window.__) === null || _b === void 0 ? void 0 : _b.call(window, 'Choose an image')) || 'Choose an image', isOpen: true, onDismiss: handleImagePickerModalDismiss, width: "90vw", children: _jsx(Box, { flexDirection: "column", height: "75vh", children: pickerComponents.map((component, index) => (_jsx(Box, { mb: 4, children: component }, index))) }) })) : null] }));
};
const InputFileHidden = styled.input `
  position: absolute;
  height: 0;
  opacity: 0;
  width: 0;
`;
//# sourceMappingURL=SingleImageUpload.js.map